import { useEffect, useMemo, useRef, useState } from 'react'

import QualityTag from 'components/OfferCard/QualityTag'
import Slider from 'components/OfferCard/Slider'
import { GAME_CARD_OFFER_TYPE, OFFER_QUALITY } from 'constants/index'
import { formatNumber } from 'utils/utils'

import ReferFriend from '../ReferFriend'
import useOfferClick from '../useOfferClick'
import useOfferImpression, { offerTileData } from './useOfferImpression'
import { getOfferMilestones, isReferralOffer, piggyBanksToDollars } from './offerRewards'

/** The media strip of a featured card, in css pixels. */
const MEDIA_HEIGHT = 124

/**
 * How many milestone bars the card draws. The count in "Across N milestones" and the total
 * reward stay the real ones; only the bars are capped, until design defines how to tell the
 * user there are more. The grid in index.css reserves the same number of slots.
 */
const VISIBLE_MILESTONES = 4

/** The rarity badge is hidden on Featured cards for now; set to true to bring it back. */
const SHOW_QUALITY = false

/**
 * Only the offers the backend actually marked as featured belong in this row.
 *
 * The marker is read instead of slicing the first few entries, so the row stays correct
 * whatever length featuredRow arrives with.
 */
export const selectFeaturedOffers = (offers = []) =>
  offers.filter((offer) => offer.offerQuality === OFFER_QUALITY.FEATURED.name)

const Pig = ({ size = 17 }) => (
  <img className="gamesRedesignPig" src="/images/pig.png" alt="" width={size} height={size} />
)

const FeaturedCard = ({
  offer,
  index,
  observer,
  experiment,
  isOnboardingTarget,
  isAutoSwiping,
}) => {
  const { ref: impressionRef, guid: offerTileViewGUID } = useOfferImpression(observer)

  const { handleClick, isDisabled } = useOfferClick({
    offer,
    index,
    type: GAME_CARD_OFFER_TYPE.FEATURED,
    offerTileViewGUID,
    experiment,
  })

  const milestones = useMemo(() => getOfferMilestones(offer), [offer])
  const visibleMilestones = milestones.slice(0, VISIBLE_MILESTONES)
  const totalPiggyBanks = milestones.reduce((total, milestone) => total + milestone.piggyBanks, 0)
  const firstPlaytimeMinutes = milestones.find((milestone) => milestone.minutes)?.minutes

  return (
    <div
      ref={impressionRef}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleClick()
        }
      }}
      data-offer={offerTileData({ offer, index, guid: offerTileViewGUID, isFeatured: true })}
      className={`gamesListCard gamesRedesignFeaturedCard ${isDisabled ? 'disabled' : ''} ${
        isOnboardingTarget ? 'gamesRedesignOnboardingTarget' : ''
      }`}
    >
      <div className="gamesRedesignFeaturedMedia">
        <Slider
          game={offer}
          isAutoSwiping={isAutoSwiping}
          mediaHeight={MEDIA_HEIGHT}
          storageKey={`video-${offer.id}`}
        />
        <div className="gamesRedesignFeaturedScrim" />
        {SHOW_QUALITY && offer.offerQuality && (
          <div className="gamesRedesignFeaturedQuality" data-testid="featured-quality">
            <QualityTag qualityName={offer.offerQuality} />
          </div>
        )}
        <div className="gamesRedesignFeaturedHeading">
          <span className="gamesRedesignFeaturedName">{offer.gameName}</span>
          {milestones.length > 0 && (
            <span className="gamesRedesignFeaturedSubtitle">
              Across {milestones.length} milestone{milestones.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {totalPiggyBanks > 0 && (
          <div className="gamesRedesignFeaturedValue">
            <span className="gamesRedesignFeaturedValueLabel">up to</span>
            <span className="gamesRedesignFeaturedValuePill">
              {formatNumber(totalPiggyBanks)}
              <Pig />
            </span>
            <span className="gamesRedesignFeaturedValueDollars">
              ≈ ${piggyBanksToDollars(totalPiggyBanks).toFixed(2)}
            </span>
          </div>
        )}
      </div>

      <div className="gamesRedesignFeaturedBody">
        {milestones.length > 0 && (
          <ul className="gamesRedesignMilestones">
            {visibleMilestones.map((milestone, milestoneIndex) => (
              <li className="gamesRedesignMilestone" key={milestoneIndex}>
                <span
                  className={`gamesRedesignMilestoneBar ${milestoneIndex === 0 ? 'current' : ''}`}
                />
                <span className="gamesRedesignMilestoneReward">
                  {formatNumber(milestone.piggyBanks)}
                  <Pig size={16} />
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* Not a <button>: the whole card is the control, a nested one would click twice. */}
        <span className="gamesRedesignFeaturedCta">
          {firstPlaytimeMinutes ? `First reward in ${firstPlaytimeMinutes} min - Play` : 'Play'}
        </span>
      </div>
    </div>
  )
}

/**
 * The part of the viewport where the carousel counts as seen: clear of the header above and
 * the tab bar below, the same allowance the legacy checkVisible used.
 */
const VISIBLE_AREA = '-60px 0px -70px 0px'

const Featured = ({ offers, observer, experiment }) => {
  const trackRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isOnScreen, setOnScreen] = useState(false)

  useEffect(() => {
    const track = trackRef.current
    if (!track) {
      return
    }

    const visibility = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), {
      rootMargin: VISIBLE_AREA,
    })
    visibility.observe(track)

    return () => visibility.disconnect()
  }, [offers])

  useEffect(() => {
    const track = trackRef.current
    if (!track) {
      return
    }

    const onScroll = () => {
      const card = track.firstElementChild
      if (!card) {
        return
      }
      const step = card.offsetWidth + parseFloat(getComputedStyle(track).columnGap || 0)
      setActiveIndex(Math.round(track.scrollLeft / step))
    }

    track.addEventListener('scroll', onScroll, { passive: true })

    return () => track.removeEventListener('scroll', onScroll)
  }, [offers])

  if (!offers?.length) {
    return null
  }

  const onboardingTargetIndex = offers.findIndex((offer) => !isReferralOffer(offer))

  return (
    <section className="gamesRedesignFeatured">
      <header className="gamesRedesignSectionHeader">
        <h2 className="gamesRedesignSectionTitle">Featured</h2>
        <span className="gamesRedesignSectionAside">Picked for you</span>
      </header>
      <div className="gamesRedesignFeaturedTrack" data-testid="featured-track" ref={trackRef}>
        {offers.map((offer, index) =>
          isReferralOffer(offer) ? (
            <div className="gamesRedesignFeaturedReferral" key={offer.id}>
              <ReferFriend referralOffer={offer} experiment={experiment} />
            </div>
          ) : (
            <FeaturedCard
              key={offer.id}
              offer={offer}
              index={index}
              observer={observer}
              experiment={experiment}
              isOnboardingTarget={index === onboardingTargetIndex}
              isAutoSwiping={isOnScreen && index === activeIndex}
            />
          )
        )}
      </div>
      {offers.length > 1 && (
        <div className="gamesRedesignFeaturedDots" aria-hidden>
          {offers.map((offer, index) => (
            <span
              key={offer.id}
              className={`gamesRedesignFeaturedDot ${index === activeIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default Featured
